import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Star, Sparkles, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/context/LanguageContext";

const TIMMA_URL = "https://bestill.timma.no/beautybykuleva";

export default function HomePage() {
  const { t } = useLanguage();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        {/* Background Image - positioned to show face on the right */}
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: "url('https://customer-assets.emergentagent.com/job_beauty-oasis-20/artifacts/wo73ogep_IMG_2917.jpeg')",
            backgroundSize: "cover",
            backgroundPosition: "75% 20%",
          }}
        >
          {/* Gradient overlay - darker on left for text readability, lighter on right to show face */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black/20" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 w-full">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={containerVariants}
            className="max-w-xl lg:max-w-2xl"
          >
            <motion.span
              variants={itemVariants}
              className="inline-flex items-center space-x-2 bg-white/90 text-[#D4A5A5] px-4 py-2 rounded-full text-sm font-medium mb-6"
            >
              <Sparkles size={16} />
              <span>Beauty by Kuleva</span>
            </motion.span>

            <motion.h1
              variants={itemVariants}
              className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 drop-shadow-lg"
            >
              {t("hero.title")}{" "}
              <span className="italic text-[#F9D5D5]">{t("hero.titleHighlight")}</span>
            </motion.h1>

            <motion.p
              variants={itemVariants}
              className="text-white/90 text-base sm:text-lg leading-relaxed mb-8 drop-shadow-md max-w-lg"
            >
              {t("hero.description")}
            </motion.p>

            <motion.div
              variants={itemVariants}
              className="flex flex-col sm:flex-row gap-4"
            >
              <a href={TIMMA_URL} target="_blank" rel="noopener noreferrer" data-testid="hero-book-btn">
                <Button className="btn-primary text-base px-8 py-6 rounded-full">
                  Book Appointment
                  <ArrowRight size={18} className="ml-2" />
                </Button>
              </a>
              <Link to="/services" data-testid="hero-services-btn">
                <Button
                  variant="outline"
                  className="border-white text-white hover:bg-white hover:text-[#D4A5A5] rounded-full px-8 py-6 bg-white/10 backdrop-blur-sm"
                >
                  {t("hero.exploreServices")}
                </Button>
              </Link>
            </motion.div>

            {/* Trust badges */}
            <motion.div
              variants={itemVariants}
              className="flex items-center gap-8 mt-12"
            >
              <div className="flex items-center gap-3">
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} size={20} className="text-[#D4A5A5]" fill="#D4A5A5" />
                  ))}
                </div>
                <div>
                  <p className="font-semibold text-white">500+</p>
                  <p className="text-xs text-white/80">Fornøyde kunder</p>
                </div>
              </div>
              <div className="hidden sm:flex items-center gap-2">
                <Award size={20} className="text-[#F9D5D5]" />
                <span className="text-sm text-white/90">{t("hero.awardWinning")}</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Services Preview - Category Cards */}
      <section className="py-20 bg-[#F9F5F6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#4A4A4A] mb-3">
              Våre tjenester
            </h2>
            <p className="text-[#8C8C8C]">Se våre mest populære behandlinger</p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {/* Bryllup - Primary Service */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <Link to="/no/wedding" data-testid="category-card-bryllup" className="block">
                <div className="bg-white rounded-3xl p-8 shadow-lg shadow-[#D4A5A5]/10 border border-[#F9F5F6] hover:shadow-xl hover:border-[#D4A5A5]/20 transition-all duration-300 h-full group">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#F9F5F6] to-[#F2E8E8] flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8 text-[#D4A5A5]">
                      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                    </svg>
                  </div>
                  <h3 className="font-['Playfair_Display'] text-xl font-bold text-[#4A4A4A] mb-2 group-hover:text-[#D4A5A5] transition-colors">
                    Bryllup
                  </h3>
                  <p className="text-[#8C8C8C] text-sm mb-4">
                    Profesjonell brudemakeup og hårstyling for din store dag.
                  </p>
                  <span className="inline-flex items-center text-[#D4A5A5] text-sm font-medium group-hover:gap-2 transition-all">
                    Se mer <ArrowRight size={14} className="ml-1" />
                  </span>
                </div>
              </Link>
            </motion.div>

            {/* Sminke og Hårstyling */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <Link to="/services#sminke-har" data-testid="category-card-sminke" className="block">
                <div className="bg-white rounded-3xl p-8 shadow-lg shadow-[#D4A5A5]/10 border border-[#F9F5F6] hover:shadow-xl hover:border-[#D4A5A5]/20 transition-all duration-300 h-full group">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#F9F5F6] to-[#F2E8E8] flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8 text-[#D4A5A5]">
                      <path d="M9 3v2m6-2v2M9 19c-3 0-6-2-6-6 0-3 2-5 4-6l1-4h8l1 4c2 1 4 3 4 6 0 4-3 6-6 6" />
                      <path d="M9 19v2a1 1 0 001 1h4a1 1 0 001-1v-2" />
                      <circle cx="12" cy="13" r="2" />
                    </svg>
                  </div>
                  <h3 className="font-['Playfair_Display'] text-xl font-bold text-[#4A4A4A] mb-2 group-hover:text-[#D4A5A5] transition-colors">
                    Sminke og Hårstyling
                  </h3>
                  <p className="text-[#8C8C8C] text-sm mb-4">
                    Brud makeup, festsminke, oppsett og styling for enhver anledning.
                  </p>
                  <span className="inline-flex items-center text-[#D4A5A5] text-sm font-medium group-hover:gap-2 transition-all">
                    Se priser <ArrowRight size={14} className="ml-1" />
                  </span>
                </div>
              </Link>
            </motion.div>

            {/* Permanent Makeup */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <Link to="/services#permanent-makeup" data-testid="category-card-permanent" className="block">
                <div className="bg-white rounded-3xl p-8 shadow-lg shadow-[#D4A5A5]/10 border border-[#F9F5F6] hover:shadow-xl hover:border-[#D4A5A5]/20 transition-all duration-300 h-full group">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#F9F5F6] to-[#F2E8E8] flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8 text-[#D4A5A5]">
                      <path d="M2 12c0-3 4-7 10-7s10 4 10 7" />
                      <path d="M12 5c-2 0-4 1-5 3" />
                      <ellipse cx="12" cy="14" rx="3" ry="2" />
                      <circle cx="12" cy="14" r="1" fill="currentColor" />
                    </svg>
                  </div>
                  <h3 className="font-['Playfair_Display'] text-xl font-bold text-[#4A4A4A] mb-2 group-hover:text-[#D4A5A5] transition-colors">
                    Permanent Makeup
                  </h3>
                  <p className="text-[#8C8C8C] text-sm mb-4">
                    Lepper, powder brows og eyeliner med langvarige resultater.
                  </p>
                  <span className="inline-flex items-center text-[#D4A5A5] text-sm font-medium group-hover:gap-2 transition-all">
                    Se priser <ArrowRight size={14} className="ml-1" />
                  </span>
                </div>
              </Link>
            </motion.div>

            {/* Øyebryn og Vippeløft */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            >
              <Link to="/services#oyebryn-vippeloft" data-testid="category-card-eyebrow" className="block">
                <div className="bg-white rounded-3xl p-8 shadow-lg shadow-[#D4A5A5]/10 border border-[#F9F5F6] hover:shadow-xl hover:border-[#D4A5A5]/20 transition-all duration-300 h-full group">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#F9F5F6] to-[#F2E8E8] flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8 text-[#D4A5A5]">
                      <path d="M2 12c2-4 6-6 10-6s8 2 10 6" />
                      <path d="M4 10l-1-3M7 8l-1-3M12 7V3M17 8l1-3M20 10l1-3" />
                      <ellipse cx="12" cy="13" rx="4" ry="2.5" />
                    </svg>
                  </div>
                  <h3 className="font-['Playfair_Display'] text-xl font-bold text-[#4A4A4A] mb-2 group-hover:text-[#D4A5A5] transition-colors">
                    Øyebryn og Vippeløft
                  </h3>
                  <p className="text-[#8C8C8C] text-sm mb-4">
                    Farging, forming, brow lift og vippeløft for perfekt blikk.
                  </p>
                  <span className="inline-flex items-center text-[#D4A5A5] text-sm font-medium group-hover:gap-2 transition-all">
                    Se priser <ArrowRight size={14} className="ml-1" />
                  </span>
                </div>
              </Link>
            </motion.div>

            {/* Sugaring */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
            >
              <Link to="/services#sugaring" data-testid="category-card-sugaring" className="block">
                <div className="bg-white rounded-3xl p-8 shadow-lg shadow-[#D4A5A5]/10 border border-[#F9F5F6] hover:shadow-xl hover:border-[#D4A5A5]/20 transition-all duration-300 h-full group">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#F9F5F6] to-[#F2E8E8] flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8 text-[#D4A5A5]">
                      <path d="M12 22c-4-2-8-6-8-12C4 6 8 2 12 2c6 0 8 4 8 8 0 6-4 10-8 12z" />
                      <path d="M12 22V12" />
                      <path d="M12 12c-2-2-4-3-4-5" />
                      <path d="M12 15c2-1 3-2 4-4" />
                    </svg>
                  </div>
                  <h3 className="font-['Playfair_Display'] text-xl font-bold text-[#4A4A4A] mb-2 group-hover:text-[#D4A5A5] transition-colors">
                    Sugaring
                  </h3>
                  <p className="text-[#8C8C8C] text-sm mb-4">
                    Skånsom hårfjerning med naturlig sukkervoks for silkemyk hud.
                  </p>
                  <span className="inline-flex items-center text-[#D4A5A5] text-sm font-medium group-hover:gap-2 transition-all">
                    Se priser <ArrowRight size={14} className="ml-1" />
                  </span>
                </div>
              </Link>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-10"
          >
            <Link to="/services" data-testid="services-view-all-btn">
              <Button className="btn-primary rounded-full px-8 py-6">
                Se alle tjenester og priser
                <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Om meg Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Portrait Photo */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-1"
            >
              <div className="relative">
                <div className="relative rounded-3xl overflow-hidden shadow-lg shadow-[#D4A5A5]/15">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_291695dd-746b-47c4-af20-bc1f9b2fc3ad/artifacts/26tagilg_Marina%20Kuleva.jpeg"
                    alt="Marina Kuleva – Beauty Master"
                    className="w-full h-auto object-cover"
                    loading="lazy"
                  />
                </div>
                {/* Award Badge */}
                <div className="absolute -bottom-4 -right-4 sm:-bottom-6 sm:-right-6 bg-white rounded-2xl shadow-xl p-4 sm:p-5">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-full bg-[#D4A5A5] flex items-center justify-center">
                      <Award size={22} className="text-white" />
                    </div>
                    <div>
                      <p className="font-['Playfair_Display'] text-base font-bold text-[#4A4A4A]">1. plass</p>
                      <p className="text-xs text-[#8C8C8C]">Grand Prix Norway 2025</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Text Content */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-2 lg:order-2"
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F9F5F6] text-[#D4A5A5] text-sm font-medium mb-6">
                <Sparkles size={16} />
                Beauty by Kuleva
              </span>
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl font-bold text-[#4A4A4A] mb-6">
                Om <span className="italic text-[#D4A5A5]">meg</span>
              </h2>
              <div className="space-y-4 text-[#8C8C8C] leading-relaxed">
                <p className="text-[#4A4A4A]">
                  Jeg heter Marina Kuleva og er en sertifisert beauty-master i Oslo med spesialisering innen brudemakeup og hårstyling.
                </p>
                <p>
                  I 2025 ble jeg kåret til vinner av <span className="text-[#D4A5A5] font-medium">Grand Prix Norway</span> og mottok 1. plass for beste brudeutseende. Denne anerkjennelsen bekrefter min erfaring, tekniske presisjon og evne til å skape et harmonisk og elegant uttrykk for brudens viktigste dag.
                </p>
                <p>
                  Jeg tilbyr profesjonell makeup og hårstyling for bryllup og spesielle anledninger, med fokus på naturlige, tidløse og fotovennlige resultater. Målet er å fremheve brudens personlighet og sikre at hun føler seg trygg, komfortabel og vakker gjennom hele dagen.
                </p>
                <p>
                  Med en helhetlig forståelse for ansiktets trekk, hudtype og styling tilpasses hvert brudeutseende individuelt. Jeg legger stor vekt på grundig konsultasjon, planlegging og detaljer, slik at resultatet blir balansert og varig.
                </p>
                <p>
                  I tillegg arbeider jeg med permanent makeup, brynsdesign, vippeløft og sugaring. Innen permanent makeup legger jeg særlig vekt på naturlige resultater, myke linjer og et uttrykk som fremhever ansiktets trekk på en harmonisk måte.
                </p>
                <p>
                  Brynsbehandlinger og vipper utføres med fokus på form, balanse og et velstelt uttrykk som passer kundens ansikt. Sugaring er en skånsom og naturlig metode for hårfjerning som gir et glatt og langvarig resultat.
                </p>
                <p>
                  Alle behandlinger utføres med høy faglig standard, god hygiene og en rolig atmosfære, slik at hver kunde føler seg trygg og ivaretatt gjennom hele behandlingen.
                </p>

                {/* Divider */}
                <div className="border-t border-[#E8D0D0] my-6"></div>

                {/* Why Choose Section */}
                <h3 className="font-['Playfair_Display'] text-xl font-bold text-[#4A4A4A] pt-2">
                  Hvorfor velge Beauty by Kuleva
                </h3>
                <ul className="space-y-2 text-[#8C8C8C]">
                  <li className="flex items-start gap-2">
                    <span className="text-[#D4A5A5] mt-1">•</span>
                    <span>Vinner av Grand Prix Norway 2025 – 1. plass for beste brudeutseende</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#D4A5A5] mt-1">•</span>
                    <span>Sertifisert beauty-master med erfaring innen makeup, hår, permanent makeup og bryn</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#D4A5A5] mt-1">•</span>
                    <span>Fokus på naturlige, elegante og tidløse resultater</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#D4A5A5] mt-1">•</span>
                    <span>Profesjonelle behandlinger utført med høy hygienestandard og individuell tilpasning</span>
                  </li>
                </ul>
                <p className="text-[#4A4A4A] font-medium">
                  Målet mitt er at hver kunde skal føle seg trygg, ivaretatt og fornøyd med et resultat som fremhever deres naturlige skjønnhet.
                </p>

                {/* Divider */}
                <div className="border-t border-[#E8D0D0] my-6"></div>

                {/* Safe Treatment Section */}
                <h3 className="font-['Playfair_Display'] text-xl font-bold text-[#4A4A4A] pt-2">
                  Trygg og profesjonell behandling
                </h3>
                <p>
                  Alle behandlinger utføres med høy hygienestandard og profesjonelle produkter. Jeg legger stor vekt på trygghet, komfort og god kommunikasjon, slik at hver kunde føler seg ivaretatt gjennom hele behandlingen.
                </p>

                {/* Divider */}
                <div className="border-t border-[#E8D0D0] my-6"></div>

                {/* Booking Section */}
                <h3 className="font-['Playfair_Display'] text-xl font-bold text-[#4A4A4A] pt-2">
                  Bestilling
                </h3>
                <p>
                  Ønsker du time til brudemakeup, permanent makeup eller andre behandlinger, kan du enkelt bestille time via bookingsystemet på nettsiden eller kontakte meg direkte.
                </p>
                <p className="text-[#4A4A4A] font-medium">
                  Det anbefales å booke bryllupsstyling i god tid. Datoene fylles raskt opp, spesielt i bryllupssesongen, så det lønner seg å være tidlig ute for å sikre ønsket dato.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-[#F9F5F6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#4A4A4A] mb-4">
              Hva våre kunder sier
            </h2>
            <p className="text-[#8C8C8C]">Ekte tilbakemeldinger fra fornøyde kunder</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Review 1 - Brudesminke */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="review-card shadow-soft"
              data-testid="review-card-0"
            >
              <div className="flex items-center mb-4 pt-8">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} size={16} className="text-[#D4A5A5]" fill="#D4A5A5" />
                ))}
              </div>
              <p className="text-[#4A4A4A] mb-4 italic text-sm leading-relaxed">
                &quot;Marina er en fantastisk dyktig mester innen brudeoppsett og sminke. Hun var veldig profesjonell, lyttet nøye til mine ønsker og skapte et helt perfekt bryllupslook for meg. Frisyren og sminken holdt hele dagen og kvelden, og jeg følte meg vakker og trygg hele tiden. Marina er rolig, hyggelig og utrolig talentfull – jeg kan varmt anbefale henne til alle kommende bruder!&quot;
              </p>
              <div className="flex items-center justify-between">
                <span className="font-semibold text-[#4A4A4A]">Ingrid O.</span>
                <span className="text-xs text-[#8C8C8C]">Brudesminke</span>
              </div>
            </motion.div>

            {/* Review 2 - Permanent Makeup */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="review-card shadow-soft"
              data-testid="review-card-1"
            >
              <div className="flex items-center mb-4 pt-8">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} size={16} className="text-[#D4A5A5]" fill="#D4A5A5" />
                ))}
              </div>
              <p className="text-[#4A4A4A] mb-4 italic text-sm leading-relaxed">
                &quot;Jeg er veldig fornøyd med permanent makeup hos Marina. Hun jobber svært nøye, hygienisk og profesjonelt, og tok seg god tid til å forklare hele prosessen. Resultatet ble naturlig og pent, akkurat slik jeg ønsket. Formen og fargen passer perfekt til ansiktet mitt. Jeg kan trygt anbefale Marina til alle som vurderer permanent makeup!&quot;
              </p>
              <div className="flex items-center justify-between">
                <span className="font-semibold text-[#4A4A4A]">Julie H.</span>
                <span className="text-xs text-[#8C8C8C]">Permanent Makeup</span>
              </div>
            </motion.div>

            {/* Review 3 - Sugaring & Vipper */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="review-card shadow-soft"
              data-testid="review-card-2"
            >
              <div className="flex items-center mb-4 pt-8">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} size={16} className="text-[#D4A5A5]" fill="#D4A5A5" />
                ))}
              </div>
              <p className="text-[#4A4A4A] mb-4 italic text-sm leading-relaxed">
                &quot;Jeg er veldig fornøyd med både sugaring og vipper hos Marina. Behandlingene var komfortable og profesjonelle, og Marina jobbet skånsomt, raskt og hygienisk. Vippene ble pene og naturlige, og resultatet passet perfekt til meg. Jeg følte meg trygg og godt ivaretatt hele tiden. Jeg kan varmt anbefale Marina!&quot;
              </p>
              <div className="flex items-center justify-between">
                <span className="font-semibold text-[#4A4A4A]">Oda L.</span>
                <span className="text-xs text-[#8C8C8C]">Sugaring & Vipper</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1512206879471-b4d119aef899?w=1200"
            alt="Makeup brushes"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#4A4A4A]/80" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              {t("cta.title")}
            </h2>
            <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
              {t("cta.description")}
            </p>
            <a href={TIMMA_URL} target="_blank" rel="noopener noreferrer" data-testid="cta-book-btn">
              <Button className="btn-primary text-base px-10 py-6 rounded-full bg-white text-[#D4A5A5] hover:bg-[#F9F5F6]">
                {t("cta.bookAppointment")}
                <ArrowRight size={18} className="ml-2" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
